<template>
  <div class="codes-page">
    <!-- Header -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">
          <span class="gradient-text">🐍 Code Hub</span>
        </h1>
        <p class="page-subtitle">{{ $t('codes_page.subtitle') }}</p>

        <!-- Search / Filter -->
        <div class="filter-bar">
          <div class="search-box">
            <span class="search-icon">🔍</span>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search codes..."
              class="search-input"
            />
          </div>

          <!-- Language Tabs -->
          <div class="lang-tabs">
            <button
              class="lang-tab"
              :class="{ active: selectedLang === '' }"
              @click="selectedLang = ''"
            >All</button>
            <button
              class="lang-tab"
              :class="{ active: selectedLang === 'ko' }"
              @click="selectedLang = 'ko'"
            >🇰🇷 한국어</button>
            <button
              class="lang-tab"
              :class="{ active: selectedLang === 'en' }"
              @click="selectedLang = 'en'"
            >🇺🇸 English</button>
          </div>

          <!-- Category Tabs -->
          <div class="category-tabs">
            <button class="cat-tab" :class="{ active: selectedCategory === '' }" @click="selectedCategory = ''">All</button>
            <button
              v-for="cat in categories"
              :key="cat"
              class="cat-tab"
              :class="{ active: selectedCategory === cat }"
              @click="selectedCategory = cat"
            >{{ cat }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Grid -->
    <div class="container">
      <div class="code-count" v-if="filteredCodes.length">
        {{ $t('codes_page.count', { n: filteredCodes.length }) }}
      </div>

      <div class="codes-grid" v-if="visibleCodes.length">
        <CodeCard
          v-for="(c, idx) in visibleCodes"
          :key="c.id"
          :code="c"
          class="animate-in"
          :style="{ animationDelay: (idx % 30) * 40 + 'ms' }"
          @select="openModal"
        />
      </div>

      <div v-else-if="!loading" class="empty-state">
        <p>{{ $t('codes_page.empty') }}</p>
      </div>

      <!-- Load More sentinel -->
      <div v-if="hasMore" ref="sentinel" class="sentinel">
        <button class="btn-load-more" @click="displayCount += 30">{{ $t('codes_page.load_more') }}</button>
      </div>
    </div>

    <!-- Modal -->
    <CodeModal
      :visible="showModal"
      :code="selectedCode"
      @close="closeModal"
      @downloaded="onDownloaded"
    />
  </div>
</template>

<script>
import CodeCard from '../components/CodeCard.vue'
import CodeModal from '../components/CodeModal.vue'

const GITHUB_STORE = 'https://raw.githubusercontent.com/xoul-project/xoul_store/main/dist'
const PAGE_SIZE = 30

export default {
  name: 'CodesView',
  components: { CodeCard, CodeModal },
  data() {
    return {
      codes: [],
      allCodes: [],   // raw from GitHub
      categories: [],
      searchQuery: '',
      selectedCategory: '',
      selectedLang: '',
      loading: false,
      showModal: false,
      selectedCode: {},
      displayCount: PAGE_SIZE,
      observer: null,
    }
  },
  computed: {
    filteredCodes() {
      let list = this.codes
      if (this.selectedLang) {
        list = list.filter(c => {
          const cLang = c.lang || 'en'
          return cLang === this.selectedLang || cLang === 'multi'
        })
      }
      if (this.selectedCategory) {
        list = list.filter(c => c.category === this.selectedCategory)
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        list = list.filter(c =>
          c.name.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q)
        )
      }
      return list
    },
    visibleCodes() {
      return this.filteredCodes.slice(0, this.displayCount)
    },
    hasMore() {
      return this.displayCount < this.filteredCodes.length
    },
  },
  watch: {
    searchQuery() { this.displayCount = PAGE_SIZE },
    selectedCategory() { this.displayCount = PAGE_SIZE },
    selectedLang() { this.displayCount = PAGE_SIZE },
    hasMore(val) {
      if (val) this.$nextTick(() => this.observeSentinel())
      else this.unobserveSentinel()
    },
  },
  mounted() {
    this.fetchCodes()
    this.$nextTick(() => this.observeSentinel())
  },
  beforeUnmount() {
    this.unobserveSentinel()
  },
  methods: {
    observeSentinel() {
      this.unobserveSentinel()
      if (!this.$refs.sentinel) return
      this.observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) this.displayCount += PAGE_SIZE
      }, { rootMargin: '200px' })
      this.observer.observe(this.$refs.sentinel)
    },
    unobserveSentinel() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },
    _resolveLang(val) {
      const lang = localStorage.getItem('xoul-locale') || 'en'
      if (typeof val === 'string') {
        try {
          const obj = JSON.parse(val)
          return obj[lang] || obj['en'] || obj['ko'] || val
        } catch { return val }
      }
      if (typeof val === 'object' && val) return val[lang] || val['en'] || val['ko'] || ''
      return val || ''
    },
    async fetchCodes() {
      this.loading = true
      try {
        const res = await fetch(`${GITHUB_STORE}/codes.json`)
        const raw = await res.json()
        this.allCodes = raw
        this.codes = raw.map(c => ({
          ...c,
          name: this._resolveLang(c.name),
          description: this._resolveLang(c.description),
        }))
        const catSet = new Set(raw.map(c => c.category).filter(Boolean))
        this.categories = [...catSet].sort()
      } catch (e) {
        console.error('Failed to fetch codes from GitHub:', e)
      }
      this.loading = false
    },
    openModal(c) {
      // allCodes already has full code — find by id
      const full = this.allCodes.find(x => x.id === c.id) || c
      this.selectedCode = {
        ...full,
        name: this._resolveLang(full.name),
        description: this._resolveLang(full.description),
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
    },
    onDownloaded(codeId) {
      const c = this.codes.find(x => x.id === codeId)
      if (c) c.downloads = (c.downloads || 0) + 1
    },
  },
}
</script>

<style scoped>
.codes-page {
  min-height: 100vh;
  background: transparent;
  padding-bottom: 80px;
}
.page-header {
  padding: 40px 0 32px;
  background: rgba(10, 10, 10, 0.5);
  border-bottom: 1px solid #1a1a1a;
}
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
.page-title { font-size: clamp(2rem, 5vw, 3rem); font-weight: 900; text-align: center; margin-bottom: 8px; }
.gradient-text {
  background: linear-gradient(135deg, var(--green) 0%, var(--teal) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.page-subtitle { text-align: center; color: var(--subtext0); font-size: 16px; margin-bottom: 32px; }

/* Filter */
.filter-bar { display: flex; flex-direction: column; align-items: center; gap: 16px; }
.search-box {
  display: flex; align-items: center; gap: 10px;
  background: rgba(13, 13, 13, 0.7); border: 1px solid #1a1a1a;
  border-radius: 8px; padding: 10px 16px; width: 100%; max-width: 420px;
  transition: border-color var(--transition);
}
.search-box:focus-within { border-color: var(--green); }
.search-icon { font-size: 16px; }
.search-input {
  flex: 1; border: none; background: transparent; outline: none;
  color: var(--text); font-size: 15px; font-family: inherit;
}
.search-input::placeholder { color: var(--overlay0); }
.lang-tabs { display: flex; gap: 6px; justify-content: center; }
.category-tabs { display: flex; gap: 6px; justify-content: center; flex-wrap: wrap; }
.lang-tab {
  padding: 6px 16px; border-radius: 20px;
  border: 1px solid var(--surface2); background: transparent;
  color: var(--subtext0); font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all var(--transition); font-family: inherit;
}
.lang-tab:hover { color: var(--text); border-color: var(--overlay1); background: var(--surface0); }
.lang-tab.active {
  background: rgba(80, 200, 120, 0.12); color: #50c878;
  border-color: rgba(80, 200, 120, 0.3);
}
.cat-tab {
  border: 1px solid var(--surface2); background: transparent;
  border-radius: 8px; padding: 6px 14px; font-size: 13px;
  color: var(--subtext0); cursor: pointer; transition: all var(--transition);
}
.cat-tab:hover { border-color: var(--green); color: var(--text); }
.cat-tab.active {
  border-color: var(--green); color: var(--green);
  background: rgba(166, 227, 161, 0.08);
}

/* Grid */
.code-count { padding: 24px 0 16px; font-size: 14px; color: var(--subtext0); }
.codes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}
.empty-state { text-align: center; padding: 80px 0; color: var(--overlay0); font-size: 16px; }
.sentinel { text-align: center; padding: 20px; }
.btn-load-more {
  background: var(--surface1); border: 1px solid var(--surface2);
  color: var(--subtext0); padding: 10px 24px; border-radius: 8px;
  cursor: pointer; transition: all var(--transition);
}
.btn-load-more:hover { border-color: var(--green); color: var(--text); }

/* ── Staggered animation ── */
.animate-in {
  opacity: 0;
  animation: fadeSlideIn 0.4s ease forwards;
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
